/* eslint-disable no-console */
import {
  Autocomplete,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  LinearProgress,
  MenuItem,
  Typography
} from '@mui/material';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import FormControlLabel from '@mui/material/FormControlLabel';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import TextField from '@mui/material/TextField';
import axios from 'axios';
import { memo, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import config from 'src/appConfig';
import { usePermissionWrapper } from 'src/components/common/hooks/usePermissionWrapper';
import { PopupComponent } from 'src/components/common/PopupComponent';
import { useGetCountriesQuery, useGetSitesQuery, useGetStudiesQuery } from 'src/components/Services/Utils';
import { AppState, store, useAppSelector } from 'src/store';
import { setDocumentDetail } from 'src/store/documentDetail';
import { setSnackbarMessage } from 'src/store/snackbarMessage';
import { usePreferedDateFormat } from 'src/util/DateFormatter';
import DocManagementService from '../documentUpload/service/DocManagementService';
import { EditMetaDataProps } from './types';

const EditMetadataModal = ({ documentProps, setDocumentMeta, setOpen }: EditMetaDataProps) => {
  const [metaData, setMetaData] = useState(documentProps);
  const [artifactNameList, setArtifactNameList] = useState<any>([]);
  const [artifactName, setArtifactName] = useState<any>();
  const [list, setList] = useState([]);
  const docService = new DocManagementService();
  const dispatch = useDispatch();
  const [isVersionCheck, setIsVersionCheck] = useState(false);
  const user: any = useAppSelector(state => state.user.user);
  const userName = `${user.firstName + ' ' + user.lastName}`;
  const { preferedDateFormat } = usePreferedDateFormat();
  const { checkPermission } = usePermissionWrapper();
  const [isProcessing, setProcessing] = useState(false);
  const selectedStudy = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Study);
  const selectedCountry = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Country);
  const selectedSite = useSelector((state: AppState) => state.searchHierarchyDetails?.data[0]?.Site);
  const etmfInstance = axios.create({ baseURL: `${config.appConfig.apietmf}` });
  const newState = store.getState();
  const headerConfig = {
    headers: {
      sponsor: newState.user.user?.sponsor_sponsor_name,
      site_token: newState.user.user?.siteToken,
      Authorization: `Bearer ${newState.user.user?.sponsorToken}`
    }
  };
  let TmfLevel =
    (selectedStudy === '--' || selectedStudy === '') &&
    (selectedCountry === '--' || selectedCountry == '') &&
    (selectedSite === '--' || selectedSite === '')
      ? '--'
      : (selectedCountry === '--' || selectedCountry == '') && (selectedSite === '--' || selectedSite === '')
      ? 'Study'
      : selectedSite === '--' && selectedCountry != '--'
      ? 'Country'
      : 'Site';
  if (TmfLevel === '--') {
    TmfLevel = 'Study';
  }

  const [currentStudy, setCurrentStudy] = useState(metaData.tmf_doc_study);
  const [currentCountry, setCurrentCountry] = useState(metaData.tmf_doc_country);
  const [currentSite, setCurrentSite] = useState(metaData.tmf_doc_site);
  const [studyList, setStudyList] = useState<any>([]);
  const [countryList, setCountryList] = useState<any>([]);
  const [siteList, setSiteList] = useState<any>([]);
  const [openConfirmExit, setOpenConfirmExit] = useState(false);

  const handleClose = () => {
    if (JSON.stringify(metaData) !== JSON.stringify(documentProps)) {
      setOpenConfirmExit(true);
    } else {
      setOpen(false);
      setOpenConfirmExit(false);
    }
  };

  const {
    isLoading: getStudyLoad,
    data: studyData,
    isError: getStudyError,
    isSuccess: getStudySuccess,
    refetch: getStudyList
  } = useGetStudiesQuery();

  useEffect(() => {
    const temp: string[] = [];
    if (studyData && studyData.length != 0) {
      studyData.map((study: any) => {
        temp.push(study.name);
      });
    }
    setStudyList(temp);
  }, [studyData]);

  const {
    isLoading,
    data: countryData,
    isError,
    isSuccess,
    refetch: getCountries
  } = useGetCountriesQuery(currentStudy);

  useEffect(() => {
    if (metaData.tmf_doc_level === 'Country' || metaData.tmf_doc_level === 'Site') {
      getCountries();
    }
    getArtifactNameListBasedStudyCountrySite(currentStudy, '--', '--');
  }, [currentStudy]);

  useEffect(() => {
    const tempC: any = [];
    if (countryData && countryData.length != 0) {
      countryData.map((country: any) => {
        tempC.push(country.name);
      });
    }
    setCountryList(tempC);
  }, [countryData]);

  const {
    isLoading: getSiteLoading,
    data: sitesData,
    isError: getSiteError,
    isSuccess: getSiteSuccess,
    refetch: getSites
  } = useGetSitesQuery({ currentStudy, currentCountry });

  useEffect(() => {
    if (metaData.tmf_doc_level === 'Site') {
      getSites();
    }
    getArtifactNameListBasedStudyCountrySite(currentStudy, currentCountry, '--');
  }, [currentCountry]);

  useEffect(() => {
    const tempS: string[] = [];
    if (sitesData && sitesData.length != 0) {
      sitesData.map((site: any) => {
        tempS.push(site.name);
      });
    }
    setSiteList(tempS);
  }, [sitesData]);

  const getArtifactNameListBasedStudyCountrySite = async (study: string, country: string, site: string) => {
    const params = {
      module_section_related: 'DocumentDetails',
      parent_entity_related: metaData.tmf_document_name
    };
    try {
      const result: any = await docService.getArtifactsList(
        metaData.tmf_doc_level ? metaData.tmf_doc_level : TmfLevel,
        study,
        country,
        site,
        params
      );

      if (result.data) {
        setArtifactNameList(result.data);
        setList(result.data);
      } else {
        setArtifactNameList([]);
        setList([]);
      }
    } catch (err: any) {
      setArtifactNameList([]);
      setList([]);
    }
  };

  useEffect(() => {
    getStudyList();
    getTmfDataResponse(metaData.tmf_doc_level);
    handleStudyChange(metaData.tmf_doc_study);
  }, []);

  useEffect(() => {
    const temp: string[] = [];
    if (studyData && studyData.length != 0) {
      studyData.map((study: any) => {
        temp.push(study.name);
      });
    }
    setStudyList(temp);
  }, [studyData]);

  const date = preferedDateFormat(metaData.tmf_doc_date.split('T')[0]);

  const handleDocumentName = (e: any) => {
    setMetaData({ ...metaData, tmf_document_name: e.target.value });
  };

  const handleDepartmentChange = (e: any) => {
    setMetaData({ ...metaData, tmf_doc_viewer_department: e.target.value });
    getTmfDepartment(e);
  };

  const getTmfDepartment = async (e: any) => {
    const result = e.target?.value
      ? await etmfInstance.get(`${config.appConfig.apietmf}/department/${e.target.value}/select`, headerConfig)
      : await etmfInstance.get(`${config.appConfig.apietmf}/department/${e}/select`, headerConfig);
    setArtifactNameList(result.data);
    setList(result.data);
  };

  const handlechangeTmfLevel = (e: any) => {
    setMetaData({
      ...metaData,
      tmf_doc_level: e.target.value,
      tmf_artifact_name: '',
      tmf_doc_milestone: '',
      tmf_doc_type: '',
      tmf_doc_subtype: '',
      tmf_artifact_number: '',
      tmf_doc_study: '',
      tmf_doc_country: '',
      tmf_doc_site: ''
    });
    getTmfDataResponse(e);
  };

  const getTmfDataResponse = async (e: any) => {
    const params = {
      module_section_related: 'DocumentDetails',
      parent_entity_related: metaData.tmf_document_name
    };
    const tmf_doc_site = currentSite ? currentSite : selectedSite;
    const tmf_doc_country = currentCountry ? currentCountry : selectedCountry;
    const tmf_doc_study = currentStudy ? currentStudy : selectedStudy;
    const result: any = await docService.getArtifactsList(
      e.target.value,
      tmf_doc_study,
      tmf_doc_country,
      tmf_doc_site,
      params
    );
    if (result.data) {
      setArtifactNameList(result.data);
      setList(result.data);
    } else {
      setArtifactNameList([]);
      setList([]);
    }
  };

  const handleStudyChange = async (e: any) => {
    setMetaData({
      ...metaData,
      tmf_doc_study: e.target.value,
      tmf_artifact_name: '',
      tmf_doc_milestone: '',
      tmf_doc_type: '',
      tmf_doc_subtype: '',
      tmf_artifact_number: '',
      tmf_doc_country: '',
      tmf_doc_site: ''
    });
  };

  const handleCountryChange = (e: any) => {
    setCurrentCountry(e.target.value);
    setMetaData({
      ...metaData,
      tmf_doc_country: e.target.value
    });
  };

  const handleSiteChange = (e: any) => {
    setMetaData({
      ...metaData,
      tmf_doc_site: e.target.value
    });
    setCurrentSite(e.target.value);
    getArtifactNameListBasedStudyCountrySite(currentStudy, currentCountry, e.target.value);
  };

  const getArtifactNameList = async (tmf_level: any) => {
    const params = {
      module_section_related: 'DocumentDetails',
      parent_entity_related: metaData.tmf_document_name
    };
    const tmf_doc_site = currentSite ? currentSite : selectedSite;
    const tmf_doc_country = currentCountry ? currentCountry : selectedCountry;
    const tmf_doc_study = currentStudy ? currentStudy : selectedStudy;
    const result: any = await docService.getArtifactsList(
      tmf_level,
      tmf_doc_study,
      tmf_doc_country,
      tmf_doc_site,
      params
    );

    if (result.data) {
      setArtifactNameList(result.data);
      setList(result.data);
    }
  };

  const handleChangeArtifactName = (e: any) => {
    if (e && e !== '') {
      setArtifactName(e);
      getArtifactNameResponse(e);
    } else {
      setMetaData({
        ...metaData,
        tmf_artifact_name: '',
        tmf_doc_milestone: '',
        tmf_doc_type: '',
        tmf_doc_subtype: '',
        tmf_artifact_number: ''
      });
    }
  };
  const handleClickArtifactName = (e: any) => {
    if ((e.target.value && e.target.value === '') || e.target.value === undefined) {
      setMetaData({
        ...metaData,
        tmf_doc_milestone: '',
        tmf_doc_type: '',
        tmf_doc_subtype: '',
        tmf_artifact_number: ''
      });
    } else {
      getArtifactNameList(metaData.tmf_doc_level);
      setMetaData({
        ...metaData,
        tmf_artifact_name: e.target.value
      });
    }
  };

  const getArtifactNameResponse = async (artifactName: any) => {
    const params = {
      module_section_related: 'DocumentDetails',
      parent_entity_related: metaData.tmf_document_name
    };
    const result = await docService.getArtifactNameResponse(
      metaData.tmf_doc_level,
      artifactName,
      selectedStudy,
      selectedCountry,
      selectedSite,
      params
    );
    setMetaData({
      ...metaData,
      tmf_artifact_number: result.data.tmf_artifact_number,
      tmf_doc_subtype: result.data.tmf_doc_subtype,
      tmf_doc_type: result.data.tmf_doc_type,
      tmf_doc_milestone: result.data.tmf_doc_milestone,
      tmf_artifact_name: artifactName
    });
  };

  const updateReqCopyValue = (event: React.ChangeEvent<HTMLInputElement>) => {
    const temp = metaData.tmf_doc_viewer_req_certified_copy == 'Y' ? 'N' : 'Y';
    setMetaData({ ...metaData, tmf_doc_viewer_req_certified_copy: temp });
  };

  const handleUpdateFile = (Meta: any) => {
    const certifiedCopy = Meta.tmf_doc_status === 'Approved' ? 'N' : Meta.tmf_doc_viewer_req_certified_copy;
    const docData: any = {
      file_master_id: Meta.fileId,
      document_data: {
        tmf_artifact_number: Meta.tmf_artifact_number ? Meta.tmf_artifact_number : Meta.tmf_doc_artifact_number,
        tmf_document_name: Meta.tmf_document_name,
        tmf_doc_type: Meta.tmf_doc_type ? Meta.tmf_doc_type : Meta.tmf_document_type,
        tmf_doc_subtype: Meta.tmf_doc_subtype ? Meta.tmf_doc_subtype : Meta.tmf_document_subtype,
        tmf_doc_viewer_req_certified_copy: certifiedCopy,
        tmf_doc_status: Meta.tmf_doc_status,
        tmf_doc_country: Meta.tmf_doc_country,
        tmf_doc_study: Meta.tmf_doc_study,
        tmf_doc_site: Meta.tmf_doc_site,
        tmf_doc_viewer_department: Meta.tmf_doc_viewer_department,
        tmf_doc_viewer_language: 'English',
        tmf_doc_viewer_approval_cycle: 0,
        tmf_doc_viewer_product_detail: '',
        tmf_doc_viewer_ref_model_version: 'TMF RM 3.2.1',
        tmf_doc_viewer_format: Meta.tmf_doc_format,
        tmf_doc_viewer_inspection_ready: 'test1',
        tmf_doc_viewer_ir_issue_type: 'test1',
        tmf_doc_level: Meta.tmf_doc_level,
        tmf_artifact_name: Meta.tmf_artifact_name,
        tmf_doc_version_number: Meta.tmf_doc_version_number,
        tmf_doc_milestone: Meta.tmf_doc_milestone,
        tmf_document_id: Meta.tmf_document_id,
        id: Meta.id,
        tmf_doc_last_modified_by: userName
      }
    };
    getDocumentDetails(Meta, docData);
  };

  const postData = async (docData: any) => {
    setProcessing(true);
    await etmfInstance
      .put(`${config.appConfig.apietmf}/masterDocument/inlineUpdate`, docData, headerConfig)
      .then(res => {
        setDocumentDetail(dispatch, [
          {
            ...metaData,
            tmf_artifact_number: metaData.tmf_artifact_number,
            tmf_doc_subtype: metaData.tmf_doc_subtype,
            tmf_doc_milestone: metaData.tmf_doc_milestone,
            tmf_doc_type: metaData.tmf_doc_type,
            tmf_artifact_name: metaData.tmf_artifact_name,
            tmf_doc_site: metaData.tmf_doc_site,
            tmf_doc_country: metaData.tmf_doc_country,
            tmf_doc_study: metaData.tmf_doc_study,
            tmf_document_name: metaData.tmf_document_name.trim(),
            tmf_doc_level: metaData.tmf_doc_level,
            tmf_doc_last_modified_by: res?.data?.document_data?.tmf_doc_last_modified_by,
            tmf_doc_status: docData?.document_data?.tmf_doc_status
          }
        ]);

        if (res.status !== 200) {
          setProcessing(false);
          store.dispatch(setSnackbarMessage('Error in uploading document', 'error'));
          setOpen(false);
          setOpenConfirmExit(false);
        } else {
          store.dispatch(setSnackbarMessage('Document is updated successfully', 'success'));
          const response = res.data.document_data;
          const finalDocData = { ...documentProps, ...response };
          setProcessing(false);
          setDocumentMeta(finalDocData);
          setOpen(false);
          setOpenConfirmExit(false);
        }
      });
  };

  // Version logic //
  const getDocumentDetails = async (metaData: any, docData: any) => {
    const is_scope = metaData.tmf_doc_study === '--' ? false : true;
    const country =
      metaData.tmf_doc_level === 'Country' || metaData.tmf_doc_level === 'Site' ? metaData.tmf_doc_country : '--';
    const site = metaData.tmf_doc_level === 'Site' ? metaData.tmf_doc_site : '--';
    const data = {
      query_filters: [
        {
          name: 'tmf_doc_level',
          value: metaData.tmf_doc_level,
          operation: 'equals'
        },
        {
          name: 'tmf_artifact_name',
          value: artifactName,
          operation: 'equals'
        }
      ],
      filter_types: ['All']
    };
    const params = {
      module_section_related: 'DocumentDetails',
      parent_entity_related: metaData.tmf_document_name
    };
    const res: any = await docService.getDocumentDetails(
      data,
      country,
      site,
      metaData.tmf_doc_study,
      is_scope,
      metaData.tmf_doc_level ? metaData.tmf_doc_level : TmfLevel,
      params
    );
    if (res.data) {
      (metaData.tmf_doc_level === res.data?.selected_data[0]?.document_data.tmf_doc_level &&
        metaData.tmf_doc_study === res.data?.selected_data[0]?.document_data.tmf_doc_study &&
        metaData.tmf_artifact_name === res.data?.selected_data[0]?.document_data.tmf_artifact_name) ||
      metaData.tmf_doc_country === res.data?.selected_data[0]?.document_data.tmf_doc_country ||
      metaData.tmf_doc_site === res.data?.selected_data[0]?.document_data.tmf_doc_site
        ? store.dispatch(
            setSnackbarMessage('Document exists with same context. Please select different values to proceed', 'error')
          )
        : postData(docData);
    } else {
      postData(docData);
    }
  };

  useEffect(() => {
    if (Number(metaData.tmf_doc_version_number) > 1.0 || metaData.tmf_doc_status === 'Approved') {
      setIsVersionCheck(true);
    } else {
      setIsVersionCheck(false);
    }
  }, []);

  return (
    <>
      <div className="document-upload-modal">
        <div className="card">
          <Grid container spacing={3}>
            <Grid item xs={8}>
              <Typography variant="h5" gutterBottom component="div">
                Edit Metadata
              </Typography>
            </Grid>
            <Grid item xs={4} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              {checkPermission('update_required_certificate_copy') && (
                <FormControlLabel
                  disabled={metaData.is_checkbox_verified || metaData.tmf_doc_status === 'Approved'}
                  value={metaData.tmf_doc_viewer_req_certified_copy}
                  control={
                    <Switch
                      color="primary"
                      onChange={updateReqCopyValue}
                      inputProps={{ 'aria-label': 'controlled' }}
                      checked={metaData.tmf_doc_viewer_req_certified_copy == 'Y' ? true : false}
                    />
                  }
                  label="Require Certified Copy?"
                  labelPlacement="end"
                />
              )}
              {/* {!checkPermission('update_required_certificate_copy') && (
              <FormControlLabel
                disabled={!checkPermission('update_required_certificate_copy')}
                value={metaData.tmf_doc_viewer_req_certified_copy}
                control={
                  <Switch
                    color="primary"
                    onChange={updateReqCopyValue}
                    inputProps={{ 'aria-label': 'controlled' }}
                    checked={metaData.tmf_doc_viewer_req_certified_copy == 'Y' ? true : false}
                  />
                }
                label="Require Certified Copy?"
                labelPlacement="end"
              />
            )} */}
            </Grid>
            <Grid item md={12}>
              {isProcessing && <LinearProgress />}
            </Grid>
            <Grid item md={4} sx={{ paddingTop: '0 !important' }}>
              <FormControl fullWidth margin="normal">
                <TextField
                  required
                  id="standard-select-currency-native"
                  label="Document Name"
                  defaultValue={metaData.tmf_document_name}
                  variant="standard"
                  onChange={e => handleDocumentName(e)}
                  inputProps={{
                    maxLength: 200
                  }}
                  value={metaData.tmf_document_name.trimLeft()}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                {/* <Autocomplete
                disabled={metaData.tmf_doc_level === 'Study' || isVersionCheck}
                disablePortal
                id={documentProps.tmf_doc_country}
                options={countryList}
                value={metaData.tmf_doc_level === 'Study' ? '' : metaData.tmf_doc_country}
                renderInput={params => (
                  <TextField
                    {...params}
                    onSelect={(e: any) => {
                      setCurrentCountry(e.target.value);
                      handleCountryChange(e);
                    }}
                    // onChange={(e: any) => handleCountryChange(e)}
                    required={metaData.tmf_doc_level === 'Country' || metaData.tmf_doc_level === 'Site'}
                    label="Country"
                    variant="standard"
                  />
                )}
              /> */}
                <TextField
                  disabled={metaData.tmf_doc_level === 'Study' || isVersionCheck}
                  required={metaData.tmf_doc_level === 'Country' || metaData.tmf_doc_level === 'Site'}
                  id={documentProps.tmf_doc_country}
                  select
                  label="Country"
                  defaultValue={metaData.tmf_doc_country}
                  value={metaData.tmf_doc_level === 'Study' ? '' : metaData.tmf_doc_country}
                  variant="standard"
                  onChange={(e: any) => handleCountryChange(e)}
                >
                  <MenuItem value=""></MenuItem>
                  {countryList.map((item: string, i: number) => (
                    <MenuItem value={item} key={i}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  disabled
                  id={documentProps.tmf_doc_version_number}
                  label="Version Number"
                  variant="standard"
                  value={parseFloat(metaData.tmf_doc_version_number).toFixed(1)}
                  onChange={e => setMetaData({ ...metaData, tmf_doc_version_number: e.target.value })}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  disabled={isVersionCheck}
                  id={documentProps.tmf_subtype}
                  label="Subtype"
                  variant="standard"
                  value={metaData.tmf_document_subtype ? metaData.tmf_document_subtype : metaData.tmf_doc_subtype}
                />
              </FormControl>
              <FormControl fullWidth margin="normal">
                <TextField
                  disabled
                  id={date}
                  label="Upload Date"
                  variant="standard"
                  defaultValue={date}
                  type="text"
                  value={preferedDateFormat(metaData.tmf_doc_date.split('T')[0])}
                  onChange={e => setMetaData({ ...metaData, tmf_doc_date: e.target.value })}
                />
              </FormControl>
            </Grid>
            <Grid item md={4} sx={{ paddingTop: '0 !important' }}>
              <FormControl fullWidth margin="normal">
                <TextField
                  disabled={isVersionCheck}
                  required
                  id={documentProps.tmf_doc_level}
                  select
                  label="TMF Level"
                  defaultValue={metaData.tmf_doc_level}
                  value={metaData.tmf_doc_level}
                  variant="standard"
                  onChange={e => {
                    handlechangeTmfLevel(e);
                  }}
                >
                  <MenuItem value="Study">Study</MenuItem>
                  <MenuItem value="Country">Country</MenuItem>
                  <MenuItem value="Site">Site</MenuItem>
                </TextField>
              </FormControl>

              <FormControl fullWidth margin="normal">
                {/* <Autocomplete
                disabled={metaData.tmf_doc_level === 'Study' || metaData.tmf_doc_level === 'Country' || isVersionCheck}
                disablePortal
                id={documentProps.tmf_doc_site}
                options={siteList}
                value={
                  metaData.tmf_doc_level === 'Study' || metaData.tmf_doc_level === 'Country'
                    ? ''
                    : metaData.tmf_doc_site
                }
                renderInput={params => (
                  <TextField
                    {...params}
                    onSelect={(e: any) => {
                      setCurrentSite(e.target.value);
                      handleSiteChange(e);
                    }}
                    onChange={e => {
                      handleSiteChange(e);
                    }}
                    required={metaData.tmf_doc_level === 'Site'}
                    label="Site"
                    variant="standard"
                  />
                )}
              /> */}
                <TextField
                  disabled={
                    metaData.tmf_doc_level === 'Study' || metaData.tmf_doc_level === 'Country' || isVersionCheck
                  }
                  required={metaData.tmf_doc_level === 'Site'}
                  id={documentProps.tmf_doc_site}
                  select
                  defaultValue={metaData.tmf_doc_site}
                  value={
                    metaData.tmf_doc_level === 'Study' || metaData.tmf_doc_level === 'Country'
                      ? ''
                      : metaData.tmf_doc_site
                  }
                  label="Site"
                  variant="standard"
                  onChange={e => {
                    handleSiteChange(e);
                  }}
                >
                  {siteList.map((item: string, i: number) => (
                    <MenuItem value={item} key={i}>
                      {item}
                    </MenuItem>
                  ))}
                </TextField>
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  disabled={isVersionCheck}
                  id={documentProps.tmf_doc_milestone}
                  label="Milestone"
                  variant="standard"
                  defaultValue={metaData.tmf_doc_milestone}
                  value={metaData.tmf_doc_milestone}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  disabled={isVersionCheck}
                  id={documentProps.tmf_artifact_number}
                  label="Artifact Number"
                  variant="standard"
                  defaultValue={metaData.tmf_artifact_number}
                  value={metaData.tmf_artifact_number}
                />
              </FormControl>
            </Grid>
            <Grid item md={4} sx={{ paddingTop: '0 !important' }}>
              <FormControl fullWidth margin="normal">
                <Autocomplete
                  disabled={isVersionCheck}
                  disablePortal
                  id={documentProps.tmf_doc_study}
                  options={studyList}
                  value={metaData.tmf_doc_study}
                  renderInput={params => (
                    <TextField
                      {...params}
                      onSelect={(e: any) => {
                        setCurrentStudy(e.target.value);
                        handleStudyChange(e);
                      }}
                      // onClick={e => {
                      //   handleClickStudyName(e);
                      // }}
                      required
                      label="Study"
                      variant="standard"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <Autocomplete
                  disabled={isVersionCheck}
                  disablePortal
                  readOnly={metaData.tmf_doc_level === ''}
                  id={documentProps.tmf_artifact_name}
                  options={list}
                  defaultValue={metaData.tmf_artifact_name}
                  value={metaData.tmf_artifact_name}
                  onChange={(event: any, newValue: string | null) => handleChangeArtifactName(newValue)}
                  renderInput={params => (
                    <TextField
                      {...params}
                      // onSelect={e => {
                      //   handleChangeArtifactName(e);
                      // }}
                      onClick={e => {
                        handleClickArtifactName(e);
                      }}
                      // onChange={e => setMetaData({ ...metaData, tmf_artifact_name: e.target.value })}
                      required
                      label="Artifact Name"
                      variant="standard"
                    />
                  )}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  disabled={isVersionCheck}
                  id={documentProps.tmf_type}
                  label="Type"
                  variant="standard"
                  value={metaData.tmf_document_type ? metaData.tmf_document_type : metaData.tmf_doc_type}
                />
              </FormControl>

              <FormControl fullWidth margin="normal">
                <TextField
                  id={documentProps.tmf_doc_viewer_department}
                  select
                  label="Department"
                  defaultValue={metaData.tmf_doc_viewer_department}
                  value={metaData.tmf_doc_viewer_department}
                  variant="standard"
                  onChange={e => {
                    handleDepartmentChange(e);
                  }}
                >
                  <MenuItem value="Clinical Operations- Site management">Clinical Operations- Site management</MenuItem>
                  <MenuItem value="Clinical Operations- Study management">
                    Clinical Operations- Study management
                  </MenuItem>
                  <MenuItem value="Data Management">Data Management</MenuItem>
                  <MenuItem value="Biometrics">Biometrics</MenuItem>
                  <MenuItem value="Clinical Development">Clinical Development</MenuItem>
                </TextField>
              </FormControl>
            </Grid>

            <Grid item md={12} className="button-actions">
              <Grid container spacing={2}>
                <Grid item md={12} display="flex" justifyContent="flex-end">
                  <Stack direction="row" spacing={2}>
                    <Button href="#contained-buttons" className="grey-btn" onClick={handleClose}>
                      Cancel
                    </Button>
                    <Button
                      onClick={() => handleUpdateFile(metaData)}
                      disabled={
                        metaData.tmf_doc_level === 'Study'
                          ? !metaData.tmf_doc_level ||
                            !metaData.tmf_doc_study ||
                            !metaData.tmf_artifact_number ||
                            !metaData.tmf_document_name.trim()
                          : metaData.tmf_doc_level === 'Country'
                          ? !metaData.tmf_doc_level ||
                            !metaData.tmf_doc_study ||
                            !metaData.tmf_doc_country ||
                            !metaData.tmf_artifact_number ||
                            !metaData.tmf_document_name.trim()
                          : metaData.tmf_doc_level === 'Site'
                          ? !metaData.tmf_doc_level ||
                            !metaData.tmf_doc_study ||
                            !metaData.tmf_doc_country ||
                            !metaData.tmf_doc_site ||
                            !metaData.tmf_artifact_number ||
                            !metaData.tmf_document_name.trim()
                          : true
                      }
                    >
                      Save
                    </Button>
                  </Stack>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        </div>
      </div>
      <PopupComponent
        open={openConfirmExit}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
        className="confirm-modal"
        child={true}
      >
        <DialogTitle id="alert-dialog-title">
          <b>Confirm Exit</b>
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            Changes you made will not be saved. Are you sure you want to exit before saving your changes?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setOpenConfirmExit(false);
            }}
            className="grey-btn"
          >
            CLOSE
          </Button>
          <Button
            onClick={() => {
              setOpen(false);
              setOpenConfirmExit(false);
            }}
          >
            EXIT
          </Button>
        </DialogActions>
      </PopupComponent>
    </>
  );
};
export default memo(EditMetadataModal);
